import { Order, OrderState, OrderType } from '@protos/v2/order';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useOrderNotifications } from '@shared/hooks/useOrderNotifications';
import { useStreamV2Status } from '@shared/hooks/useStreamV2Status';
import { toastifyService } from '@services/ToastifyService';
import { Status } from '@services/StreamV2';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useOrderNotifications as useOrderNotificationsContext } from '@shared/contexts/OrderNotificationsContexts';
import { checkIsDTUser } from '@utils/permissions';

export const GlobalOrderNotifications = () => {
  const user = useUserContext();
  const [isReadyToSubscribe, setIsReadyToSubscribe] = useState(false);
  const lastPlayedRef = useRef<number>(0);
  const COOLDOWN_MS = 1000;
  const status = useStreamV2Status();
  const { addNotification, isSoundEnabled } = useOrderNotificationsContext();

  useEffect(() => {
    setIsReadyToSubscribe(status === Status.Connected);
  }, [status]);

  useEffect(() => {
    toastifyService.setCurrentUserId(user?.id);
  }, [user?.id]);

  const isUserDT = checkIsDTUser(user);

  const handleOrderUpdate = useCallback(
    (order: Order) => {
      if (order.order_state === OrderState.FILLED || order.order_state === OrderState.PARTIALLY_FILLED) {
        addNotification(order);
        if (isSoundEnabled) {
          const now = Date.now();
          if (now - lastPlayedRef.current >= COOLDOWN_MS) {
            try {
              const isQuotebookOrder = order.order_type === OrderType.LIMIT_MAKER && order.exchange === 'onyx';
              const audio = new Audio(isQuotebookOrder ? '/assets/sounds/happy-ping.mp3' : '/assets/filled.mp3');
              audio.play().catch(error => {
                console.error(error);
              });
              lastPlayedRef.current = now;
            } catch (error) {
              console.error(error);
            }
          }
        }
      }
    },
    [addNotification, isSoundEnabled]
  );

  const allAccounts = useMemo(() => {
    if (!user) return [];
    if (isUserDT) {
      return ['DT'];
    } else {
      return [...(user.trading_accounts || []), ...(user.abn_trad_accounts || [])];
    }
  }, [user, isUserDT]);

  useOrderNotifications(allAccounts, handleOrderUpdate, isReadyToSubscribe);

  return null;
};
